import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { request } from '../utils/request';
import ReusableTable from '../components/Table/ReusableTable';
import ReusableHeader from '../components/Header/ReusableHeader';

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ id: null, name: '', description: '' });
  const [isEditing, setIsEditing] = useState(false);

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    const data = await request('roles', { method: 'get' });
    const normalized = data.map(r => ({
      id: r._id,
      name: r.name,
      description: r.description,
      createdAt: r.createdAt,
      updatedAt: r.updatedAt,
    }));
    setRoles(normalized);
  };

  const handleOpen = (role = null) => {
    if (role) {
      setFormData(role);
      setIsEditing(true);
    } else {
      setFormData({ id: null, name: '', description: '' });
      setIsEditing(false);
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (isEditing) {
      await request('roles', {
        method: 'put', params: formData.id, body: {
          name: formData.name,
          description: formData.description,
        }
      });
    } else {
      await request('roles', {
        method: 'post', body: {
          name: formData.name,
          description: formData.description,
        }
      });
    }
    fetchRoles();
    handleClose();
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter roles based on search query
  const filteredRoles = roles.filter(r =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedRoles = filteredRoles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const columns = [
    { id: 'index', label: '#', minWidth: 50, render: (row, idx) => page * rowsPerPage + idx + 1 },
    { id: 'name', label: 'ROLE' },
    {
      id: 'actions', label: 'ACTIONS', align: 'right', render: row => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <IconButton onClick={() => handleOpen(row)} sx={{ color: '#1F6FEB', p: 0.5 }} size="small">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton sx={{ color: '#D6323A', p: 0.5 }} size="small">
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <ReusableHeader
        title="Role Master"
        breadcrumbs={[{ label: "Role Master" }]}
      />

      <Box sx={{ mt: 2 }}>
        <ReusableTable
          columns={columns}
          data={paginatedRoles}
          page={page}
          rowsPerPage={rowsPerPage}
          total={filteredRoles.length}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSearch={setSearchQuery}
          searchPlaceholder="Search roles..."
          filters={
            <>
              <Button
                startIcon={<FilterListIcon />}
                variant="outlined"
                size="small"
                sx={{
                  color: 'var(--slate)',
                  borderColor: 'var(--hairline)',
                  borderRadius: '8px',
                  textTransform: 'none',
                  backgroundColor: 'var(--canvas)',
                  height: '40px'
                }}
              >
                Filters
              </Button>
              <Button
                startIcon={<SettingsIcon />}
                variant="outlined"
                size="small"
                sx={{
                  color: 'var(--slate)',
                  borderColor: 'var(--hairline)',
                  borderRadius: '8px',
                  textTransform: 'none',
                  backgroundColor: 'var(--canvas)',
                  height: '40px'
                }}
              >
                Configurations
              </Button>
            </>
          }
          actions={
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{ backgroundColor: '#1F6FEB', '&:hover': { backgroundColor: '#155bb5' }, py: 1, px: 2, borderRadius: '8px', textTransform: 'none', height: '40px' }}
              onClick={() => handleOpen()}
            >
              Add Role
            </Button>
          }
        />
      </Box>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ backgroundColor: '#1F6FEB', color: '#fff', fontSize: '1rem', py: 1.5 }}>
          {isEditing ? 'Edit Role' : 'Add Role'}
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 4 }}>
          <Stack spacing={3}>
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleClose} sx={{ color: 'text.secondary', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            size="small"
            sx={{ backgroundColor: '#1F6FEB', '&:hover': { backgroundColor: '#155bb5' }, textTransform: 'none', px: 3 }}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
